import React, { useEffect, useState } from "react"
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Button,
  Snackbar,
} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"

interface NavigatorExtended extends Navigator {
  deviceMemory?: number
  hardwareConcurrency: number
  gpu?: string
  connection?: {
    effectiveType: string
    downlink: number
    rtt: number
    saveData: boolean
  }
  userAgentData?: {
    platform: string
    brands: Array<{ brand: string; version: string }>
    mobile: boolean
  }
}

const DeviceDiagnostics: React.FC = () => {
  const [copySuccess, setCopySuccess] = useState(false)
  const [diagnosticData, setDiagnosticData] = useState<Record<string, any>>({})

  useEffect(() => {
    const nav = navigator as NavigatorExtended

    const getData = async () => {
      const data: Record<string, any> = {
        // Basic browser information
        userAgent: nav.userAgent,
        appVersion: nav.appVersion,
        gpu: nav.gpu,
        platform: nav.platform,
        vendor: nav.vendor,
        language: nav.language,
        languages: nav.languages,

        // Screen properties
        screen: {
          width: window.screen.width,
          height: window.screen.height,
          availWidth: window.screen.availWidth,
          availHeight: window.screen.availHeight,
          colorDepth: window.screen.colorDepth,
          pixelDepth: window.screen.pixelDepth,
          orientation: window.screen.orientation.type,
          devicePixelRatio: window.devicePixelRatio,
        },

        // Connection information
        connection: nav.connection
          ? {
            effectiveType: nav.connection.effectiveType,
            downlink: nav.connection.downlink,
            rtt: nav.connection.rtt,
            saveData: nav.connection.saveData,
          }
          : "Not Available",

        // Hardware information
        hardwareConcurrency: nav.hardwareConcurrency,
        deviceMemory: nav.deviceMemory,
        maxTouchPoints: nav.maxTouchPoints,

        // Browser capabilities
        cookiesEnabled: nav.cookieEnabled,
        serviceWorkerSupport: "serviceWorker" in navigator,

        // Window properties
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },

        // Media capabilities
        mediaCapabilities: {
          vibration: "vibrate" in navigator,
          bluetooth: "bluetooth" in navigator,
          geolocation: "geolocation" in navigator,
          webShare: "share" in navigator,
        },
      }

      // Try to get user agent client hints if available
      if (nav.userAgentData) {
        data.userAgentData = {
          platform: nav.userAgentData.platform,
          brands: nav.userAgentData.brands,
          mobile: nav.userAgentData.mobile,
        }
      }

      setDiagnosticData(data)
    }

    getData()
  }, [])

  const copyToClipboard = () => {
    navigator.clipboard.writeText(JSON.stringify(diagnosticData, null, 2))
    setCopySuccess(true)
  }

  const renderValue = (value: any): React.ReactNode => {
    if (value === null) return "null"
    if (value === undefined) return "undefined"
    if (typeof value === "boolean") return value.toString()
    if (typeof value === "object") {
      return <pre>{JSON.stringify(value, null, 2)}</pre>
    }
    return value.toString()
  }

  // Group data into logical sections
  const organizeData = (data: Record<string, any>) => {
    return {
      "System Information": {
        platform: data.platform,
        userAgent: data.userAgent,
        appVersion: data.appVersion,
        vendor: data.vendor,
        language: data.language,
        languages: data.languages,
        userAgentData: data.userAgentData,
      },
      "Hardware Capabilities": {
        gpu: data.gpu,
        hardwareConcurrency: data.hardwareConcurrency,
        deviceMemory: data.deviceMemory,
        maxTouchPoints: data.maxTouchPoints,
        screen: data.screen,
      },
      "Network & Connectivity": {
        connection: data.connection,
        cookiesEnabled: data.cookiesEnabled,
        serviceWorkerSupport: data.serviceWorkerSupport,
      },
      "Display & Viewport": {
        viewport: data.viewport,
        screen: data.screen,
      },
      "Media & Device Features": {
        mediaCapabilities: data.mediaCapabilities,
      }
    }
  }

  const renderSection = (title: string, data: any) => (
    <Accordion
      defaultExpanded={title === "System Information"}
    // sx={{
    //   '&:before': {
    //     display: 'none',
    //   },
    //   boxShadow: 'none',
    //   backgroundColor: 'transparent',
    // }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: 'background.paper',
          borderRadius: 1,
          minHeight: '48px',
          '&.Mui-expanded': {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            minHeight: '48px',
          },
          '& .MuiAccordionSummary-content': {
            margin: '8px 0',
            '&.Mui-expanded': { margin: '8px 0' }
          }
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>{title}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 1.5, pt: 2 }}>
        <Box sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
          gap: 1.5
        }}>
          {Object.entries(data).map(([key, value]) => (
            <Card
              key={key}
              variant="outlined"
              sx={{
                backgroundColor: 'background.paper',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 1,
                }
              }}
            >
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography
                  variant="caption"
                  color="primary"
                  sx={{
                    fontWeight: 600,
                    display: 'block',
                    mb: 0.5
                  }}
                >
                  {key}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}
                >
                  {renderValue(value)}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  )

  return (
    <Card sx={{ m: 2 }}>
      <CardHeader
        title={
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Device Diagnostics
          </Typography>
        }
        action={
          <Button
            startIcon={<ContentCopyIcon />}
            onClick={copyToClipboard}
            variant="contained"
            size="small"
            sx={{ borderRadius: 1.5 }}
          >
            Copy All Data
          </Button>
        }
        sx={{ pb: 1 }}
      />
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {Object.entries(organizeData(diagnosticData)).map(([section, data]) =>
            renderSection(section, data)
          )}
        </Box>
      </CardContent>
      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(false)}
        message="Diagnostic data copied to clipboard"
      />
    </Card>
  )
}

export default DeviceDiagnostics
